import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Form } from 'react-bootstrap';
import { reduxForm, Field, change, formValueSelector, isDirty } from 'redux-form';
import cns from 'classnames';
import RawImageUpload from '../../../../../shared/components/formComponents/inputs/RawImageUpload';
import ImageUpload from '../../../../../shared/components/formComponents/inputs/ImageUpload';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import HexInput from '../../../../../shared/components/formComponents/groups/HexInput';
import { VerbRichTextEditor } from '../../../../../shared/components/formComponents/VerbRichTextEditor';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';

class CompanyBrandForm extends Component {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        changeFormValue: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        onSubmit: PropTypes.func,
        onCancel: PropTypes.func,
        submitting: PropTypes.bool,
        error: PropTypes.string,
        SSOFlow: PropTypes.bool,
        customBranding: PropTypes.bool,
        isVerbAdmin: PropTypes.bool,
        currentValues: PropTypes.object,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func
    };

    componentDidUpdate(prevProps) {
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    onImageUpload = (imageUrl, options) => {
        const { onSubmit } = this.props;
        const { field } = options;
        const params = {
            [field]: imageUrl
        };
        return onSubmit(params);
    };

    onRawImageUpload = (field) => (imageUrl) => {
        const { onSubmit } = this.props;
        const params = {
            [field]: imageUrl
        };
        return onSubmit(params);
    };

    restoreDefaults = () => {
        const { changeFormValue } = this.props;
        changeFormValue({ field: 'whiteLabelColors.navigationPrimaryBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.navigationPrimaryFont', value: '' });
        changeFormValue({ field: 'whiteLabelColors.navigationSecondaryBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.navigationSecondaryFont', value: '' });
        changeFormValue({ field: 'whiteLabelColors.primaryActionPrimaryBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.primaryActionPrimaryFont', value: '' });
        changeFormValue({ field: 'whiteLabelColors.primaryActionSelectedBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.primaryActionSelectedFont', value: '' });
        changeFormValue({ field: 'whiteLabelColors.secondaryActionPrimaryBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.secondaryActionPrimaryFont', value: '' });
        changeFormValue({ field: 'whiteLabelColors.secondaryActionSelectedBackground', value: '' });
        changeFormValue({ field: 'whiteLabelColors.secondaryActionSelectedFont', value: '' });
        changeFormValue({ field: 'whiteLabelLogo', value: '' });
    };

    render() {
        const {
            submitting,
            handleSubmit,
            onCancel,
            error,
            customBranding,
            isVerbAdmin,
            currentValues: { whiteLabelColors = {} } = {},
            SSOFlow
        } = this.props;

        const loginPageSplashImageFormField = !SSOFlow ? (
            <Field
                component={ImageUpload}
                name={'splashImage'}
                label={'Sign-in Page Splash Image'}
                className={cns('row company-form-field', { hidden: !SSOFlow })}
                cropProp={{ width: 150, height: 150, type: 'square' }}
                description={
                    'This image will appear on your company page, as shown in the Company Page Thumbnail below.'
                }
                inputClassName={'col-12 col-sm-6'}
                buttonClassName={'col-12 col-sm-6'}
                styleProps={{ horizontal: true }}
            />
        ) : (
            <Field
                component={ImageUpload}
                name={'SSOSplashImage'}
                label={'Sign-in Page Splash Image'}
                className={cns('row company-form-field', { hidden: !SSOFlow })}
                cropProp={{ width: 160, height: 90, type: 'rectangle' }}
                description={
                    'This image will appear on your company SSO sign in page, as shown in the Company Page Thumbnail below.'
                }
                inputClassName={'col-12 col-sm-6'}
                buttonClassName={'col-12 col-sm-6'}
                styleProps={{ horizontal: true }}
            />
        );

        return (
            <Form className={'companyForm container'}>
                {SSOFlow ? (
                    <Field
                        component={ImageUpload}
                        name={'image'}
                        label={'Logo'}
                        className={'row company-form-field'}
                        cropProp={{ width: 150, height: 150, type: 'square' }}
                        description={
                            "Please upload an image of your organization's logo in one of the following formats: .png, .jpg, .jpeg, .gif, or .svg."
                        }
                        inputClassName={'col-12 col-sm-6'}
                        buttonClassName={'col-12 col-sm-6'}
                        styleProps={{ horizontal: true }}
                        onImageUpload={this.onImageUpload}
                    />
                ) : null}

                {loginPageSplashImageFormField}
                <Field
                    name={'splashTextColor'}
                    label={'Sign-in Page Text Color'}
                    placeholder={''}
                    className={cns('row company-form-field', { hidden: !SSOFlow })}
                    options={[
                        { value: 'light', label: 'light' },
                        { value: 'dark', label: 'dark' }
                    ]}
                    component={BasicSelect}
                    description={
                        'This color will appear on your company page, as shown in the Company Page Thumbnail below.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                <Field
                    name={'splashBackgroundColor'}
                    label={'Sign-in Page Background Color'}
                    placeholder={''}
                    className={cns('row company-form-field', { hidden: !SSOFlow })}
                    options={[
                        { value: 'light', label: 'light' },
                        { value: 'dark', label: 'dark' }
                    ]}
                    component={BasicSelect}
                    description={
                        'This color will appear on your company page, as shown in the Company Page Thumbnail below.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                {SSOFlow && (
                    <Field
                        component={VerbRichTextEditor}
                        name={'splashHeader'}
                        label={'Sign-in Splash Header'}
                        className={'row company-form-field'}
                        disableLinkButton={true}
                        description={
                            'When members of your company sign in to Verb, they will be greeted with this header text.'
                        }
                        inputClassName={'col-12 col-sm-10 no-padding'}
                        styleProps={{ horizontal: true }}
                    />
                )}
                {SSOFlow && (
                    <Field
                        component={VerbRichTextEditor}
                        name={'splashText'}
                        label={'Sign-in Splash Text'}
                        className={'row company-form-field'}
                        disableLinkButton={true}
                        description={
                            'When members of your company sign in to Verb, they will be greeted with this brief description of the ways in which Verb is to serve your company.'
                        }
                        inputClassName={'col-12 col-sm-10 no-padding'}
                        styleProps={{ horizontal: true }}
                    />
                )}
                {customBranding || isVerbAdmin ? (
                    <>
                        <Field
                            component={RawImageUpload}
                            name={'whiteLabelLogo'}
                            label={'Navigation Logo'}
                            className={'row company-form-field nav-logo-upload'}
                            inputClassName={'col-12 col-sm-6'}
                            description={
                                'Upload your company’s logo here. For best results, please ensure your logo is a rectangular PNG at least 50px in height.'
                            }
                            styleProps={{ horizontal: true }}
                            squareImage={false}
                            onImageUpload={this.onRawImageUpload('whiteLabelLogo')}
                        />

                        <Field
                            name={'whiteLabelColors.navigationPrimaryBackground'}
                            label={'Navigation Background Color'}
                            placeholder={'261C4C'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={'This is the background color of the top navigation bar on Verb.'}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.navigationPrimaryBackground}
                            defaultColor={'261C4C'}
                        />
                        <Field
                            name={'whiteLabelColors.navigationPrimaryFont'}
                            label={'Navigation Primary Font Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={'This is the color of the text and icons in the top navigation bar.'}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.navigationPrimaryFont}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.navigationSecondaryBackground'}
                            label={'Navigation Secondary Background Color'}
                            placeholder={'5EC9B9'}
                            className={'row company-form-field'}
                            description={
                                'This is the color of the text and icons in the top navigation bar when hovered.'
                            }
                            component={HexInput}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.navigationSecondaryBackground}
                            defaultColor={'5EC9B9'}
                        />
                        <Field
                            name={'whiteLabelColors.navigationSecondaryFont'}
                            label={'Navigation Secondary Font Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            description={
                                'This is the color of the text and icons in the top navigation bar when selected.'
                            }
                            component={HexInput}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.navigationSecondaryFont}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.primaryActionPrimaryBackground'}
                            label={'Button Primary Background Color'}
                            placeholder={'BB266C'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={
                                'This is the background color of the primary buttons on Verb. These buttons are larger and more prominent than secondary buttons.'
                            }
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.primaryActionPrimaryBackground}
                            defaultColor={'BB266C'}
                        />
                        <Field
                            name={'whiteLabelColors.primaryActionPrimaryFont'}
                            label={'Button Primary Font Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={'This is the color of the text and icons on primary buttons.'}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.primaryActionPrimaryFont}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.primaryActionSelectedBackground'}
                            label={'Button Selected Background Color'}
                            placeholder={'A82261'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={
                                'This is the background color of the primary button when a learner hovers over it.'
                            }
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.primaryActionSelectedBackground}
                            defaultColor={'A82261'}
                        />
                        <Field
                            name={'whiteLabelColors.primaryActionSelectedFont'}
                            label={'Button Selected Font Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            component={HexInput}
                            maxLength={'6'}
                            description={
                                'This is the color of the text and icons on the primary button when a learner hovers over it.'
                            }
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.primaryActionSelectedFont}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.secondaryActionPrimaryBackground'}
                            label={'Secondary Button Background Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            component={HexInput}
                            maxLength={'6'}
                            description={
                                'This is the outline color of the secondary buttons on Verb. These buttons are smaller and less prominent than primary buttons, and their background color is transparent.'
                            }
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.secondaryActionPrimaryBackground}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.secondaryActionPrimaryFont'}
                            label={'Secondary Button Font Color'}
                            placeholder={'612F8A'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={'This is the color of the text and icons on secondary buttons.'}
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.secondaryActionPrimaryFont}
                            defaultColor={'612F8A'}
                        />
                        <Field
                            name={'whiteLabelColors.secondaryActionSelectedBackground'}
                            label={'Secondary Button Selected Background Color'}
                            placeholder={'FFFFFF'}
                            className={'row company-form-field'}
                            component={HexInput}
                            description={
                                'This is the outline color of the secondary button when a learner hovers over it.'
                            }
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.secondaryActionSelectedBackground}
                            defaultColor={'FFFFFF'}
                        />
                        <Field
                            name={'whiteLabelColors.secondaryActionSelectedFont'}
                            label={'Secondary Button Selected Font Color'}
                            placeholder={'261C4C'}
                            className={'row company-form-field last-hex-input'}
                            component={HexInput}
                            description={
                                'This is the color of the text and icons on the secondary button when a learner hovers over it.'
                            }
                            maxLength={'6'}
                            inputClassName={'col-12 col-sm-4 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                            color={whiteLabelColors.secondaryActionSelectedFont}
                            defaultColor={'261C4C'}
                        />
                        <div className={'col-12 restore-row'}>
                            <a className={'restore-link'} onClick={this.restoreDefaults}>
                                Restore Defaults
                            </a>
                        </div>
                    </>
                ) : null}
                <br />
                <div className={'col-12 formButtons'}>
                    <VerbButton
                        fontAwesomeIcon={'fa-save'}
                        textId={'BUTTON_SAVE'}
                        defaultMessage={'SAVE'}
                        onClick={(values) => {
                            this.setState({ formSubmitted: true });
                            handleSubmit(values);
                        }}
                        disabled={submitting}
                    />
                    <VerbButton
                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                        fontAwesomeIcon={'fa-ban'}
                        textId={'BUTTON_CANCEL'}
                        defaultMessage={'CANCEL'}
                        onClick={onCancel}
                        disabled={submitting}
                    />
                </div>
                <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
            </Form>
        );
    }
}

export const CompanyBrandFormComponent = CompanyBrandForm;

const selector = formValueSelector('CompanyBrand'); // <-- same as form name

export const makeMapStateToProps = () => {
    const mapStateToProps = (state, ownProps) => {
        const {
            image,
            splashImage,
            splashHeader,
            splashText,
            splashTextColor,
            splashBackgroundColor,
            SSOFlow = false,
            SSOProviders = {},
            SSOSplashImage = '',
            customBranding = false,
            whiteLabelLogo = '',
            whiteLabelColors = {
                navigationPrimaryBackground: '',
                navigationPrimaryFont: '',
                navigationSecondaryBackground: '',
                navigationSecondaryFont: '',
                primaryActionPrimaryBackground: '',
                primaryActionPrimaryFont: '',
                primaryActionSelectedBackground: '',
                primaryActionSelectedFont: '',
                secondaryActionPrimaryBackground: '',
                secondaryActionPrimaryFont: '',
                secondaryActionSelectedBackground: '',
                secondaryActionSelectedFont: ''
            }
        } = ownProps.company;
        const { auth: { isVerbAdmin } = {} } = state;

        const currentWhiteLabelColorsValue = selector(state, 'whiteLabelColors');
        return {
            dirty: isDirty('CompanyBrand'),
            initialValues: {
                image,
                splashImage,
                splashHeader,
                splashText,
                splashTextColor,
                splashBackgroundColor,
                SSOSplashImage,
                whiteLabelLogo,
                whiteLabelColors
            },
            SSOFlow,
            SSOProviders,
            customBranding,
            isVerbAdmin,
            currentValues: { whiteLabelColors: currentWhiteLabelColorsValue }
        };
    };

    return mapStateToProps;
};

export const mapDispatchToProps = (dispatch) => ({
    changeFormValue: (params) => dispatch(change('CompanyBrand', params.field, params.value))
});

const CompanyBrandFormValidation = {
    form: 'CompanyBrand',
    destroyOnUnmount: false,
    enableReinitialize: true
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanyBrandFormValidation)
)(CompanyBrandForm);
